/**
 * Sidecar-metadata parsing. Asset packs ship a license.txt / readme / credits
 * file describing author + license + source URLs; this heuristically extracts
 * that so the user can apply it to a folder instead of retyping. Pure text in,
 * { authors?, sources?, license? } out — no DOM, no file reading here.
 */

const SIDECAR_RE = /^(licen[sc]e|readme|credits?|authors?|copyright)(\.(txt|md|rst))?$|\.meta$/i;

/** Is this filename a likely metadata sidecar? */
export function isSidecarName(name) {
    return SIDECAR_RE.test(String(name || "").trim());
}

/** Sidecar file nodes directly inside a folder node. */
export function findSidecarFiles(node) {
    return (node?.files || []).filter((f) => isSidecarName(f.name));
}

// Ordered most-specific → least so "CC BY-NC-SA" wins over "CC BY". Every value
// is one the License dropdown can represent — the parser never emits free text.
const LICENSE_PATTERNS = [
    [/\bCC0\b|public\s*domain|\bunlicense\b/i, "CC0"],
    [/\bCC[\s-]?BY[\s-]?NC[\s-]?SA\b/i, "CC BY-NC-SA"],
    [/\bCC[\s-]?BY[\s-]?NC\b/i, "CC BY-NC"],
    [/\bCC[\s-]?BY[\s-]?ND\b/i, "CC BY-ND"],
    [/\bCC[\s-]?BY[\s-]?SA\b/i, "CC BY-SA"],
    [/\bCC[\s-]?BY\b/i, "CC BY"],
    [/all\s+rights\s+reserved/i, "Owned"],
];

// Conservative: split only on commas/semicolons. " and "/"&" are too ambiguous
// ("Sand and Stone Studios", "Tom & Jerry") and shredded single names.
function splitList(s) {
    return String(s || "")
        .split(/[,;]/)
        .map((x) => x.trim())
        .filter(Boolean)
        .slice(0, 8);
}

/**
 * Extract { authors?, sources?, license? } from sidecar text. Conservative —
 * only returns a field when it finds a confident signal; the user reviews
 * before applying.
 */
export function parseSidecarText(text) {
    const t = String(text || "");
    const out = {};

    // License: an explicit "License: X" line is authoritative — match it against
    // the known list ONLY (never write the raw line, which the dropdown can't
    // hold). With no explicit line, fall back to a bare scan of the whole text.
    // Either way we only ever emit a representable value, or none.
    const licLine = t.match(/licen[sc]e\s*[-:]\s*([^\n\r]+)/i);
    const lic = licLine
        ? LICENSE_PATTERNS.find(([re]) => re.test(licLine[1]))
        : LICENSE_PATTERNS.find(([re]) => re.test(t));
    if (lic) out.license = lic[1];

    // Author(s): "Author(s): X" or "Created/Made/Artwork by X". `\b` keeps "art"
    // from matching the tail of words like "smart"/"depart".
    const authLine =
        t.match(/authors?\s*[-:]\s*([^\n\r]+)/i) ||
        t.match(/\b(?:created|made|artwork|art)\s+by\b\s*[-:]?\s*([^\n\r]+)/i);
    if (authLine) {
        const authors = splitList(authLine[1]);
        if (authors.length) out.authors = authors;
    }

    // Source URLs.
    const urls = t.match(/https?:\/\/[^\s)>\]"']+/g);
    if (urls) out.sources = [...new Set(urls)].slice(0, 5);

    return out;
}
